import { Link } from "react-router";
import { Badge, DataTable, Quantity, Tooltip, type BadgeTone, type DataTableColumn, type IconName } from "@/components";
import { Decimal } from "@/lib/decimal";
import { formatFractionAsPercent, formatMass } from "@/lib/format";
import type { CoverageLine, CoverageStatus } from "../api";
import styles from "./CoverageTable.module.css";

const STATUS_LOOK: Record<CoverageStatus, { label: string; tone: BadgeTone; icon: IconName }> = {
  ok: { label: "Enough", tone: "success", icon: "check" },
  short: { label: "Short", tone: "warning", icon: "alertTriangle" },
  missing: { label: "None in stock", tone: "danger", icon: "x" },
};

function lotsText(line: CoverageLine): string {
  return line.lots
    .map((lot) => {
      const mass = formatMass(lot.balance_g);
      return Decimal.from(lot.active_fraction).compare(1) < 0
        ? `${lot.code}: ${mass} of a ${formatFractionAsPercent(lot.active_fraction, { minDecimals: 0, maxDecimals: 2 })} dilution`
        : `${lot.code}: ${mass}`;
    })
    .join(", ");
}

/** Needed vs on-the-shelf neat grams per material, for one formula at one batch size. */
export function CoverageTable({ lines, caption }: { lines: CoverageLine[]; caption: string }) {
  const columns: DataTableColumn<CoverageLine>[] = [
    {
      id: "material",
      header: "Material",
      width: "wide",
      cell: (line) => (
        <Link to={`/materials/${line.material_id}`} className={styles.link}>
          {line.material_name}
        </Link>
      ),
    },
    {
      id: "required",
      header: "Needed (g)",
      numeric: true,
      cell: (line) => <Quantity value={line.required_g} unit="g" hideUnit />,
    },
    {
      id: "available",
      header: "On the shelf (g neat)",
      numeric: true,
      cell: (line) =>
        line.lots.length ? (
          <Tooltip content={lotsText(line)}>
            <button type="button" className={styles.tip}>
              <Quantity value={line.available_g} unit="g" hideUnit />
            </button>
          </Tooltip>
        ) : (
          <Quantity value={line.available_g} unit="g" hideUnit />
        ),
    },
    {
      id: "short",
      header: "Short by (g)",
      numeric: true,
      cell: (line) =>
        line.status === "ok" ? (
          <span className={styles.subtle}>—</span>
        ) : (
          <span className={styles.short}>
            <Quantity value={line.shortfall_g} unit="g" hideUnit />
          </span>
        ),
    },
    {
      id: "status",
      header: "Status",
      cell: (line) => {
        const look = STATUS_LOOK[line.status];
        return (
          <Badge tone={look.tone} icon={look.icon}>
            {look.label}
          </Badge>
        );
      },
    },
  ];
  return (
    <DataTable
      caption={caption}
      columns={columns}
      rows={lines}
      getRowId={(line) => line.material_id}
      defaultSort={undefined}
    />
  );
}
